import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useBasicEventInfoQuery } from './basicEventInfo.graphql.generated';
import { useSegmentGroupCallForEvent } from '@shared/core/analytics/useSegmentGroupCall';
import { EXCLUDED_EVENT_HANDLES } from '@shared/constants/excludedEventHandles';

import type { EventInfo, EventInfoData } from './EventInfo.types';
import { InternalEventInfoProvider } from './internalEventInfoContext';
import { useIdentify } from '@shared/core/analytics/useIdentify';
import { useEventUserRole } from '@shared/components/AuthProvider/AuthProvider.utils';
import { isInIframe } from '@shared/utils/isInIframe';

const isPreviewing = isInIframe();

/**
 * Loads event information and makes this data available to all its children.
 *
 * For more details, see [eventInfo/README.md](./README.md).
 */
export const EventInfoProvider: React.FC<{ eventHandle: string | undefined }> = ({ eventHandle: eventHandleProp, children }) => {
  const [eventHandle, setEventHandle] = useState<string | undefined>(() => eventHandleProp);

  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const { isAdmin } = useEventUserRole();

  const isExcludedEventHandle = eventHandle ? EXCLUDED_EVENT_HANDLES.has(eventHandle) : false;
  const shouldSkipBasicEventInfo = !eventHandle || isExcludedEventHandle;

  const { data, error, loading } = useBasicEventInfoQuery({
    batchMode: 'off',
    variables: { eventHandle: eventHandle || '', isAdmin: isPreviewing && isAdmin },
    fetchPolicy: 'cache-and-network',
    skip: shouldSkipBasicEventInfo,
  });
  const eventByName = data?.eventByName;

  const { executeSegmentGroupCallForEvent } = useSegmentGroupCallForEvent();
  executeSegmentGroupCallForEvent(eventByName?.firebaseId, {
    postgresEventId: eventByName?.id,
    eventType: eventByName?.info.eventType,
  });

  const { identify } = useIdentify();
  useEffect(() => {
    const firebaseId = eventByName?.firebaseId;
    if (firebaseId) {
      identify({
        lastFirebaseEventId: firebaseId,
      });
    }
  }, [eventByName, executeSegmentGroupCallForEvent, identify]);

  const eventInfoData = useMemo<EventInfoData | undefined>(() => {
    const eventByName = data?.eventByName;
    if (!eventByName) {
      return undefined;
    }

    const { firebaseId, id, info, firebaseCreateAt, productVerticals, settings, published, websiteState, featureFlags } = eventByName;
    const {
      eventType,
      eventDisplayName,
      eventPassword,
      fianceeFirstName,
      fianceeLastName,
      ownerFirstName,
      ownerLastName,
      locale,
      locationInfo,
      finalizedDate,
      story,
      qanda,
      tentativeDate,
    } = info;
    return {
      eventId: id,
      eventFirebaseId: firebaseId,
      eventType,
      eventDisplayName,
      eventPassword: eventPassword ?? undefined,
      ownerFirstName,
      ownerLastName,
      fianceeFirstName,
      fianceeLastName,
      locale,
      locationInfo,
      firebaseCreateAt,
      productVerticals,
      finalizedDate,
      tentativeDate,
      story,
      qanda,
      published: published,
      settings,
      websiteState,
      featureFlags: (featureFlags || []).filter(Boolean) as string[],
    };
  }, [data?.eventByName]);

  const eventInfo = useMemo<EventInfo>(() => {
    return isInitialLoad
      ? {
          loading: true,
        }
      : {
          loading,
          eventHandle,
          error,
          eventInfo: eventInfoData,
          finalizedEventDate: eventInfoData?.finalizedDate?.timestamp ? new Date(eventInfoData.finalizedDate.timestamp) : null,
        };
  }, [error, eventHandle, eventInfoData, isInitialLoad, loading]);

  const onFinishLoadingEventInfo = useCallback(() => {
    setIsInitialLoad(false);
  }, []);

  return (
    <InternalEventInfoProvider
      value={{
        setEventHandle,
        eventInfo,
        onFinishLoadingEventInfo,
      }}
    >
      {children}
    </InternalEventInfoProvider>
  );
};
